import React, { useState, useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import hero_img_1 from "../assets/hero_img_1.png";
import hero_img_2 from "../assets/hero_img_2.png";
import hero_img_3 from "../assets/hero_img_3.png";
import hero_logo from "../assets/hero_logo_new.png";
import qoute_1 from "../assets/qoute_1.png";
import qoute_2 from "../assets/qoute_2.png";
import whiteCurve from "../assets/white_curve.svg";
import { useI18n } from "../i18n/I18nProvider";

export default function Hero() {
  const exploreUrl = "https://ekatra-summarizer.glide.page/dl/2476c3";
  const { t } = useI18n();

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  return (
    <section
      id="home"
      className="relative w-full bg-ekatraOrange pt-8 md:pt-20 pb-[240px] md:pb-[430px] overflow-visible rounded-b-[60px] mb-12"
    >
      {/* ================= CONTENT ================= */}
      <div className="relative z-10 md:px-12 px-6 grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* LEFT TEXT */}
        <div>
          <h1 className="lg:max-w-[670px] text-[32px] sm:text-[36px] md:text-[40px] lg:text-[56px] leading-tight text-[#0C2E4E] font-thin">
            {t("hero.title.prefix")}{" "}
            <span className="font-bold">{t("hero.title.worldKnowledge")}</span>{" "}
            {t("hero.title.connector")}
            <br />
            {t("hero.title.our")}{" "}
            <span className="font-bold">{t("hero.title.language")}</span>
          </h1>

          <p className="mt-3 text-[#0C2E4E] text-[16px] opacity-80 font-medium max-sm:text-center">
            {t("hero.subtitle")}
          </p>
        </div>

        {/* RIGHT SIDE */}
        <div className="flex flex-col items-start max-sm:items-center">
          {/* QUOTE WITH IMAGES */}
          <div className="relative italic text-[#0C2E4E] text-[24px] md:text-[28px] lg:text-[32px] leading-[1.4] font-medium text-center">
            <img
              src={qoute_1}
              alt="Opening quote"
              className="absolute left-2 -top-4  w-6 h-6 lg:w-12 lg:h-12"
            />
            {t("hero.quote.line1")}
            <br />
            {t("hero.quote.line2")}
            <img
              src={qoute_2}
              alt="Closing quote"
              className="inline-block ml-2 w-6 h-6 lg:w-12 lg:h-12 relative top-4"
            />
          </div>

          {/* BUTTON + ICON (two elements) */}
          <div className="mt-8 inline-flex items-center gap-0 md:self-start">
            <a
              href={exploreUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 bg-ekatraLightBlue text-white rounded-full text-lg font-semibold shadow-md hover:bg-[#072B49] transition"
            >
              {t("hero.cta")}
            </a>
            <a
              href={exploreUrl}
              target="_blank"
              rel="noreferrer"
              className="relative left-[-8px] w-[52px] h-[52px] bg-ekatraLightBlue text-white rounded-full shadow-md flex items-center justify-center hover:bg-[#072B49] transition"
              aria-label="Open Explore More in a new tab"
            >
              <ArrowUpRight size={22} strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>

      {/* ORIGINAL LAYOUT - FOR DESKTOP */}
      {!isMobile && (
        <>
          <div className="absolute bottom-[-8px] lg:bottom-[-4px] left-0 w-full z-40">
            <img
              src={whiteCurve}
              alt="white curve"
              className="w-full h-auto object-cover"
              style={{ zIndex: 100 }}
            />

            {/* CIRCULAR LOGO BADGE (centered in the notch) */}
            <div className="absolute left-1/2 -translate-x-1/2 lg:-translate-y-2 md:-translate-y-0 bottom-0 w-28 h-28 md:w-52 md:h-52 rounded-full z-30 overflow-hidden shadow-md">
              <img
                src={hero_logo}
                alt="logo"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
          </div>

          {/* ================= BOOKS sit slightly behind the white curve ================= */}
          <div
            className="absolute left-1/2 transform -translate-x-[44%] flex items-end justify-center z-30 pointer-events-none w-[94%] md:w-[85%] lg:w-[78%]"
            style={{ transformOrigin: "center bottom" }}
          >
            <img
              src={hero_img_2}
              alt="book-left"
              className="
                lg:w-[w-280px] lg:h-[280px]
                md:w-[w-200px] md:h-[200px]
                max-md:w-[w-160px] max-md:h-[160px]
                object-cover
                transform lg:translate-x-24
                md:translate-x-24
                max-md:translate-x-20
                max-md:-translate-y-6
                lg:-translate-y-10
                md:translate-y-16
                -rotate-8
                z-30
              "
            />

            <img
              src={hero_img_1}
              alt="book-center"
              className="
                lg:w-[w-430px] lg:h-[430px]
                md:w-[w-340px] md:h-[340px]
                max-md:w-[w-240px] max-md:h-[246px]
                transform lg:-translate-x-14
                md:-translate-x-10
                max-lg:translate-y-24
                max-md:-translate-y-0
                max-md:-translate-x-8
                object-cover
                z-50
              "
            />
            <img
              src={hero_img_3}
              alt="book-right"
              className="
                lg:w-[w-280px] lg:h-[280px]
                md:w-[w-200px] md:h-[200px]
                max-md:w-[w-160px] max-md:h-[160px]
                object-cover
                transform lg:-translate-x-60
                md:-translate-x-48
                lg:-translate-y-10
                md:translate-y-16
                max-md:-translate-x-32
                max-md:-translate-y-6
                -rotate-8
                z-50
              "
            />
          </div>
        </>
      )}

      {/* MOBILE LAYOUT */}
      {isMobile && (
        <>
          <div className="absolute bottom-[-8px] lg:bottom-[-4px] left-0 w-full z-40">
            <img
              src={whiteCurve}
              alt="white curve"
              className="w-full h-auto object-cover"
              style={{ zIndex: 100 }}
            />

            {/* CIRCULAR LOGO BADGE (centered in the notch) */}
            <div className="absolute left-1/2 -translate-x-1/2 lg:-translate-y-2 md:-translate-y-0 bottom-0 w-28 h-28 md:w-52 md:h-52 rounded-full z-30 overflow-hidden shadow-md">
              <img
                src={hero_logo}
                alt="logo"
                className="w-full h-full object-cover rounded-full"
              />
            </div>
          </div>

          {/* ================= BOOKS sit slightly behind the white curve ================= */}
          <div
            className="absolute left-1/2 transform -translate-x-[44%] flex items-end justify-center z-30 pointer-events-none w-[94%] md:w-[85%] lg:w-[78%]"
            style={{ transformOrigin: "center bottom" }}
          >
            <img
              src={hero_img_2}
              alt="book-left"
              className="
                lg:w-[w-280px] lg:h-[280px]
                md:w-[w-200px] md:h-[200px]
                max-md:w-[w-160px] max-md:h-[160px]
                object-cover
                transform lg:translate-x-24
                md:translate-x-24
                max-md:translate-x-20
                max-md:translate-y-16
                lg:-translate-y-10
                md:translate-y-16
                -rotate-8
                z-30
              "
            />

            <img
              src={hero_img_1}
              alt="book-center"
              className="
                lg:w-[430px] lg:h-[430px]
                md:w-[340px] md:h-[340px]
                max-md:w-[180px] max-md:h-[180px]
                transform lg:-translate-x-14
                max-md:translate-y-16
                max-lg:translate-y-24
                max-md:translate-y-16
                max-md:-translate-x-8
                object-cover
                z-50
              "
            />
            <img
              src={hero_img_3}
              alt="book-right"
              className="
                lg:w-[w-280px] lg:h-[280px]
                md:w-[w-200px] md:h-[200px]
                max-md:w-[w-160px] max-md:h-[160px]
                object-cover
                transform lg:-translate-x-60
                md:-translate-x-48
                lg:-translate-y-10
                md:translate-y-16
                max-md:-translate-x-32
                max-md:translate-y-16
                -rotate-8
                z-50
              "
            />
          </div>
        </>
      )}
    </section>
  );
}
